import { useMemo, useState } from 'react';
import { FaTools } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import BtnPopover, { type CloseFn, type Placement } from '../../../components/btn-popover';
import MeetingNotes from '../../../components/meeting-notes';
import { useHotel } from '../../../context/hotel-context';
import { useUser } from '../../../context/user-context';
import { useIsShared } from '../../reports/hooks/use-report-location';
import { useMeeting } from '../context/meeting-context';
import { useMeetingTools } from '../hooks/use-meeting-tools';
import { ActionItemView } from './action-item-view';
import { AgendaView } from './agenda-view';
import { MeetingForms } from './meeting-forms';
import { TeamView } from './team-view';

export enum FormView {
  ACTION_ITEMS = 'actionItems',
  AGENDA = 'agenda',
  NOTES = 'notes',
  TEAM = 'team',
}

const MeetingTools = () => {
  const location = useLocation();
  const { isShared } = useIsShared();
  const { hotel } = useHotel();
  const { user } = useUser();
  const [showForm, setShowForm] = useState<boolean>(false);
  const [view, setView] = useState(FormView.AGENDA);
  const { meetingTools } = useMeetingTools(hotel?.brand_code);
  const { meeting, setMeeting } = useMeeting();

  const showMeetingNotes = !location.pathname.includes('reports');

  const offsetH = useMemo(() => {
    switch (view) {
      case FormView.AGENDA:
        return 20;
      case FormView.ACTION_ITEMS:
        return 25;
      case FormView.NOTES:
        return 30;
      default:
        return 20;
    }
  }, [view]);

  const handleViewChange = (nextView: FormView) => {
    setView(nextView);
    setShowForm(false);
  };

  const panelContent = (props?: { close?: CloseFn }) => (
    <div className='flex flex-col h-full p-4 bg-white rounded-lg drop-shadow-2xl'>
      <div className='flex justify-between'>
        <select
          value={view}
          onChange={(e) => handleViewChange(e.target.value as FormView)}
          className='w-fit font-semibold mr-2'
        >
          <option value={FormView.AGENDA}>Agenda</option>
          <option value={FormView.TEAM}>Team</option>
          <option value={FormView.ACTION_ITEMS}>Action Items</option>
          {showMeetingNotes && <option value={FormView.NOTES}>Meeting Notes</option>}
        </select>
        {!showForm && !isShared && view !== FormView.NOTES ? (
          <button
            type='button'
            className='px-2 mx-2 text-sm text-green-600 border border-green-500 rounded-md hover:text-green-700 hover:border-green-600'
            onClick={() => setShowForm(true)}
          >
            Add {view === FormView.AGENDA || view === FormView.ACTION_ITEMS ? 'Item' : 'Member'}
          </button>
        ) : view !== FormView.NOTES ? (
          <button
            type='button'
            className='px-2 mx-2 text-sm text-red-600 border border-red-500 rounded-md'
            onClick={() => setShowForm(false)}
          >
            Back
          </button>
        ) : null}
      </div>
      {showForm && !isShared ? (
        <MeetingForms brandCode={hotel?.brand_code} close={props?.close} view={view} />
      ) : (
        <div className='mt-2 h-full'>
          {view === FormView.AGENDA && (
            <AgendaView isTool={true} meeting={meeting} meetingTools={meetingTools} />
          )}
          {view === FormView.TEAM && (
            <TeamView isTool={true} meeting={meeting} meetingTools={meetingTools} />
          )}
          {view === FormView.ACTION_ITEMS && <ActionItemView meetingTools={meetingTools} />}
          {view === FormView.NOTES && showMeetingNotes && (
            <MeetingNotes
              brandCode={hotel?.brand_code}
              meeting={meeting}
              parentName='meeting-tools'
              setMeeting={setMeeting}
              userId={user?.id as string | undefined}
            />
          )}
        </div>
      )}
    </div>
  );

  const btnProps = {
    btnClass: 'focus:outline-none',
    btnIconClass: 'mx-2',
    btnIcon: FaTools,
    btnSpanClass: 'flex items-center text-gray-50 hover:text-gray-300 text-sm',
    btnTooltip: 'Meeting Tools',
    offsetH,
    offsetV: -20,
    panelContent,
    popPlacement: 'bottom' as Placement,
  };

  return <BtnPopover {...btnProps} />;
};

export default MeetingTools;
